import { useState } from 'react';

import {
  tokensApi,
  type ApiToken,
  type CreateApiTokenResponse,
} from '@/api/queries/tokens';
import {
  Button,
  Icon,
  IconButton,
  StatusPill,
  Table,
  Tag,
  type TableColumn,
} from '@/components/ui';

import { NewTokenModal } from './NewTokenModal';
import { TokenRevealBanner } from './TokenRevealBanner';

export function ApiTokensPage() {
  const { data: tokens = [], isPending } = tokensApi.useList();
  const revoke = tokensApi.useRevoke();
  const [modalOpen, setModalOpen] = useState(false);
  const [reveal, setReveal] = useState<CreateApiTokenResponse | null>(null);

  const columns: TableColumn<ApiToken>[] = [
    {
      id: 'name',
      header: 'Name',
      cell: (t) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <Icon name="shield" size={13} style={{ color: 'var(--text-3)' }} />
          <span style={{ fontWeight: 500 }}>{t.name}</span>
        </span>
      ),
    },
    {
      id: 'prefix',
      header: 'Token',
      width: 200,
      cell: (t) => (
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-3)' }}>
          {t.prefix}…
        </span>
      ),
    },
    {
      id: 'scopes',
      header: 'Scopes',
      width: 220,
      cell: (t) => (
        <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
          {t.scopes.map((s) => (
            <Tag key={s} tone="brand">
              {s}
            </Tag>
          ))}
        </span>
      ),
    },
    {
      id: 'created',
      header: 'Created',
      width: 130,
      cell: (t) => formatDate(t.createdAt),
    },
    {
      id: 'lastUsed',
      header: 'Last used',
      width: 130,
      cell: (t) =>
        t.lastUsedAt ? (
          formatDate(t.lastUsedAt)
        ) : (
          <span style={{ color: 'var(--text-4)' }}>never</span>
        ),
    },
    {
      id: 'expires',
      header: 'Expires',
      width: 130,
      cell: (t) =>
        t.expiresAt ? (
          formatDate(t.expiresAt)
        ) : (
          <span style={{ color: 'var(--text-4)' }}>never</span>
        ),
    },
    {
      id: 'status',
      header: 'Status',
      width: 130,
      cell: (t) => {
        if (t.revokedAt) return <StatusPill status="dnc">Revoked</StatusPill>;
        if (t.expiresAt && new Date(t.expiresAt) < new Date()) {
          return <StatusPill status="inactive">Expired</StatusPill>;
        }
        return <StatusPill status="active">Active</StatusPill>;
      },
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      <header className="page-header">
        <div>
          <h1 className="page-title">API tokens</h1>
          <div className="page-subtitle">
            Personal access tokens authenticate first-party tooling like the LinkedIn
            Chrome extension. Each token is scoped and shown exactly once at creation.
          </div>
        </div>
        <div className="page-actions">
          <Button
            variant="primary"
            leftIcon={<Icon name="plus" size={13} />}
            onClick={() => setModalOpen(true)}
          >
            New token
          </Button>
        </div>
      </header>

      {reveal && (
        <TokenRevealBanner raw={reveal.raw} onDismiss={() => setReveal(null)} />
      )}

      <Table<ApiToken>
        columns={columns}
        rows={tokens}
        rowKey={(t) => t.id}
        loading={isPending}
        emptyMessage="No tokens yet — create one to authenticate the LinkedIn extension or other tooling."
        rowActions={(t) =>
          t.revokedAt ? null : (
            <IconButton
              icon="trash"
              iconSize={13}
              size="sm"
              label="Revoke token"
              className="row-action danger"
              onClick={() => {
                if (
                  confirm(
                    `Revoke token "${t.name}"? Any tooling using it will stop working immediately.`,
                  )
                ) {
                  revoke.mutate(t.id);
                }
              }}
            />
          )
        }
      />

      <NewTokenModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreated={(res) => setReveal(res)}
      />
    </div>
  );
}

function formatDate(value?: string): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
